import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardHeader from '@/components/Header/DashboardHeader';
import { Footer } from '@/components/organisms/Footer';
import { apiService } from '@/services/api';
import type { MarketListing, MarketReportReason, MarketReview } from '@/types/market';
import { DEFAULT_AVATAR, Stars, formatPrice, timeAgo } from './MarketShared';
import styles from './Market.module.scss';

const REPORT_REASONS: { value: MarketReportReason; label: string }[] = [
  { value: 'spam', label: 'Spam' },
  { value: 'scam', label: 'Scam / fraud' },
  { value: 'prohibited', label: 'Prohibited item' },
  { value: 'offensive', label: 'Offensive' },
  { value: 'wrong_category', label: 'Wrong category' },
  { value: 'other', label: 'Other' },
];

export default function MarketListingDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [listing, setListing] = useState<MarketListing | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activePhoto, setActivePhoto] = useState(0);
  const [contacting, setContacting] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savingFav, setSavingFav] = useState(false);

  const [reviews, setReviews] = useState<MarketReview[]>([]);
  const [reviewAvg, setReviewAvg] = useState(0);
  const [reviewCount, setReviewCount] = useState(0);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);

  // Trust & safety
  const [myPersonId, setMyPersonId] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState<MarketReportReason | null>(null);
  const [reportNote, setReportNote] = useState('');
  const [submittingReport, setSubmittingReport] = useState(false);
  const [reportDone, setReportDone] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const [confirmBlock, setConfirmBlock] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const mounted = useRef(true);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => { if (mounted.current) setToast(null); }, 3000);
  };

  useEffect(() => {
    mounted.current = true;
    if (!id) return;
    (async () => {
      setLoading(true);
      try {
        const l = await apiService.mktGetListing(id);
        if (!mounted.current) return;
        setListing(l);
        setSaved(!!l?.saved);
        setError(null);
        // Load seller reviews
        if (l?.seller?.id) {
          try {
            const r = await apiService.mktGetPersonReviews(l.seller.id);
            if (mounted.current) { setReviews(r.reviews); setReviewAvg(r.avg); setReviewCount(r.count); }
          } catch { /* non-fatal */ }
        }
      } catch (e: any) {
        if (mounted.current) setError(e?.message || 'Failed to load this listing.');
      } finally {
        if (mounted.current) setLoading(false);
      }
      // Identity (to hide report/block on my own listing) + block state — best-effort.
      try {
        const me = await apiService.mktGetMe();
        if (mounted.current) setMyPersonId(me?.id ?? null);
      } catch { /* non-fatal */ }
    })();
    return () => { mounted.current = false; };
  }, [id]);

  // Reflect whether this seller is already blocked.
  useEffect(() => {
    if (!listing?.seller?.id) return;
    (async () => {
      try {
        const blocks = await apiService.mktGetBlocks();
        if (mounted.current) setBlocked(blocks.includes(listing.seller.id));
      } catch { /* non-fatal */ }
    })();
  }, [listing?.seller?.id]);

  const contactSeller = async () => {
    if (!listing || contacting) return;
    setContacting(true);
    try {
      const thread = await apiService.mktContactSeller(listing.id);
      navigate(`/customer/market/messages?thread=${encodeURIComponent(thread.id)}`);
    } catch (e: any) {
      setError(e?.message || 'Could not start a chat with the seller.');
    } finally {
      if (mounted.current) setContacting(false);
    }
  };

  const toggleSaveFav = async () => {
    if (!listing || savingFav) return;
    const wasSaved = saved;
    setSaved(!wasSaved); // optimistic
    setSavingFav(true);
    try {
      if (wasSaved) await apiService.mktUnsaveListing(listing.id);
      else await apiService.mktSaveListing(listing.id);
    } catch (e: any) {
      if (mounted.current) {
        setSaved(wasSaved); // revert
        showToast(e?.message || 'Could not update your saved ads.');
      }
    } finally {
      if (mounted.current) setSavingFav(false);
    }
  };

  const submitReview = async () => {
    if (!listing || rating < 1 || submittingReview) return;
    setSubmittingReview(true);
    setReviewError(null);
    try {
      const review = await apiService.mktPostPersonReview(listing.seller.id, {
        rating,
        comment: comment.trim() || undefined,
        listingId: listing.id,
      });
      if (!mounted.current) return;
      setReviews((prev) => [review, ...prev]);
      const newCount = reviewCount + 1;
      setReviewAvg((reviewAvg * reviewCount + rating) / newCount);
      setReviewCount(newCount);
      setRating(0);
      setComment('');
    } catch (e: any) {
      if (mounted.current) {
        // Verified reviews: backend returns 403 when the reviewer hasn't contacted this provider.
        const msg = e?.status === 403
          ? (e?.message || "You can review a provider after you've contacted them.")
          : (e?.message || 'Failed to submit review.');
        setReviewError(msg);
      }
    } finally {
      if (mounted.current) setSubmittingReview(false);
    }
  };

  const submitReport = async () => {
    if (!listing || !reportReason || submittingReport) return;
    setSubmittingReport(true);
    setReportError(null);
    try {
      await apiService.mktReportListing(listing.id, reportReason, reportNote.trim() || undefined);
      if (!mounted.current) return;
      setReportDone(true);
    } catch (e: any) {
      if (mounted.current) setReportError(e?.message || 'Could not submit your report. Please try again.');
    } finally {
      if (mounted.current) setSubmittingReport(false);
    }
  };

  const closeReport = () => {
    setReportOpen(false);
    setReportReason(null);
    setReportNote('');
    setReportDone(false);
    setReportError(null);
  };

  const blockSeller = async () => {
    if (!listing || blocking) return;
    setBlocking(true);
    try {
      await apiService.mktBlockPerson(listing.seller.id);
      if (!mounted.current) return;
      setBlocked(true);
      setConfirmBlock(false);
      showToast('Seller blocked. You won’t see their listings anymore.');
    } catch (e: any) {
      if (mounted.current) { setConfirmBlock(false); showToast(e?.message || 'Could not block this seller.'); }
    } finally {
      if (mounted.current) setBlocking(false);
    }
  };

  const isOwnListing = myPersonId != null && listing?.seller?.id === myPersonId;

  return (
    <>
      <DashboardHeader />
      <div className={styles.container}>
        <div className={styles.topBar}>
          <button type="button" className={styles.backButton} onClick={() => navigate(-1)}>← Back</button>
          {!loading && listing && (
            <>
              <span className={styles.spacer} />
              <button type="button" className={styles.secondaryBtn} onClick={toggleSaveFav}
                disabled={savingFav} aria-pressed={saved}
                aria-label={saved ? 'Remove from saved ads' : 'Save this ad'}>
                {saved ? '♥ Saved' : '♡ Save'}
              </button>
            </>
          )}
          {!loading && listing && !isOwnListing && (
            <>
              <div className={styles.overflowWrap}>
                <button type="button" className={styles.overflowBtn} aria-label="More options"
                  aria-haspopup="menu" aria-expanded={menuOpen} onClick={() => setMenuOpen((o) => !o)}>⋯</button>
                {menuOpen && (
                  <>
                    <div style={{ position: 'fixed', inset: 0, zIndex: 20 }} onClick={() => setMenuOpen(false)} />
                    <div className={styles.overflowMenu} role="menu">
                      <button type="button" className={styles.overflowItem} role="menuitem"
                        onClick={() => { setMenuOpen(false); setReportOpen(true); }}>🚩 Report listing</button>
                      {blocked ? (
                        <button type="button" className={styles.overflowItem} role="menuitem" disabled>✓ Seller blocked</button>
                      ) : (
                        <button type="button" className={`${styles.overflowItem} ${styles.overflowItemDanger}`} role="menuitem"
                          onClick={() => { setMenuOpen(false); setConfirmBlock(true); }}>🚫 Block seller</button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        {loading && <p className={styles.muted}><span className={styles.spinner} /> Loading…</p>}
        {error && !loading && <p className={styles.error}>{error}</p>}

        {!loading && listing && (
          <>
            {/* Gallery */}
            <div className={styles.gallery}>
              {listing.photos && listing.photos.length > 0 ? (
                <>
                  <img className={styles.galleryMain} src={listing.photos[activePhoto]} alt={listing.title} />
                  {listing.photos.length > 1 && (
                    <div className={styles.thumbs}>
                      {listing.photos.map((p, i) => (
                        <img
                          key={i}
                          src={p}
                          alt={`${listing.title} ${i + 1}`}
                          className={`${styles.thumb} ${i === activePhoto ? styles.thumbActive : ''}`}
                          onClick={() => setActivePhoto(i)}
                        />
                      ))}
                    </div>
                  )}
                </>
              ) : (
                !listing.video && <div className={styles.galleryMainPlaceholder}>{listing.kind === 'service' ? '🛠️' : '📦'}</div>
              )}
              {listing.video && (
                <video
                  className={styles.galleryMain}
                  src={listing.video}
                  controls
                  playsInline
                  preload="metadata"
                  style={{ marginTop: listing.photos && listing.photos.length > 0 ? 10 : 0 }}
                />
              )}
            </div>

            <div className={styles.detailPrice}>
              {formatPrice(listing.price, listing.currency)}
              {listing.isNegotiable && listing.price != null && (
                <span className={styles.badge} style={{ marginLeft: 10, verticalAlign: 'middle' }}>Negotiable</span>
              )}
            </div>
            <h1 className={styles.detailTitle}>{listing.title}</h1>
            <div className={styles.detailMeta}>
              {[listing.area, listing.city].filter(Boolean).join(', ') || '—'} · {timeAgo(listing.createdAt)}
              {listing.status && listing.status !== 'active' && (
                <span className={`${styles.badge} ${styles.badgeSold}`} style={{ marginLeft: 8 }}>{listing.status}</span>
              )}
            </div>

            {listing.description && <p className={styles.detailDesc}>{listing.description}</p>}

            <button type="button" className={styles.primaryBtn} onClick={contactSeller} disabled={contacting || blocked}
              style={{ width: '100%', marginTop: 6 }}>
              {blocked ? 'Seller blocked' : contacting ? 'Starting chat…' : '💬 Contact seller'}
            </button>

            {/* Seller card */}
            <div className={styles.panel}>
              <div className={styles.personCard} onClick={() => navigate(`/customer/market/providers?person=${encodeURIComponent(listing.seller.id)}`)}
                role="button" tabIndex={0}>
                <img className={styles.avatar} src={listing.seller.profilePic || DEFAULT_AVATAR} alt={listing.seller.displayName}
                  onError={(e) => { (e.currentTarget as HTMLImageElement).src = DEFAULT_AVATAR; }} />
                <div>
                  <div className={styles.personName}>{listing.seller.displayName}</div>
                  <div className={styles.personSub}>{listing.seller.city || 'Seller'}</div>
                  <Stars value={reviewAvg} count={reviewCount} />
                </div>
              </div>
            </div>

            {/* Reviews */}
            <div className={styles.panel}>
              <h2 className={styles.sectionTitle} style={{ marginTop: 0 }}>
                Reviews <Stars value={reviewAvg} count={reviewCount} />
              </h2>

              {/* Leave a review */}
              <div className={styles.field}>
                <label className={styles.label}>Leave a review for this seller</label>
                <div className={styles.starPick}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} type="button" className={`${styles.starBtn} ${n <= rating ? styles.starBtnActive : ''}`}
                      onClick={() => setRating(n)} aria-label={`${n} star`}>★</button>
                  ))}
                </div>
                <textarea className={styles.textarea} placeholder="Share your experience (optional)"
                  value={comment} onChange={(e) => setComment(e.target.value)} style={{ minHeight: 70 }} />
                {reviewError && <p className={styles.error}>{reviewError}</p>}
                <button type="button" className={styles.secondaryBtn} onClick={submitReview}
                  disabled={rating < 1 || submittingReview} style={{ marginTop: 8 }}>
                  {submittingReview ? 'Submitting…' : 'Submit review'}
                </button>
              </div>

              {reviews.length === 0 ? (
                <p className={styles.muted}>No reviews yet.</p>
              ) : (
                reviews.map((r) => (
                  <div key={r.id} className={styles.reviewItem}>
                    <div className={styles.reviewHead}>
                      <img className={styles.avatar} style={{ width: 32, height: 32 }}
                        src={r.reviewer.profilePic || DEFAULT_AVATAR} alt={r.reviewer.displayName}
                        onError={(e) => { (e.currentTarget as HTMLImageElement).src = DEFAULT_AVATAR; }} />
                      <span className={styles.personName} style={{ fontSize: 14 }}>{r.reviewer.displayName}</span>
                      <span className={`${styles.badge} ${styles.badgeActive}`} title="This reviewer contacted the seller">✓ Verified</span>
                      <span className={styles.stars}>{'★'.repeat(r.rating)}</span>
                      <span className={styles.reviewDate}>{timeAgo(r.createdAt)}</span>
                    </div>
                    {r.comment && <p className={styles.reviewComment}>{r.comment}</p>}
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>

      {/* Report sheet */}
      {reportOpen && (
        <div className={styles.sheetOverlay} onClick={closeReport}>
          <div className={styles.sheet} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            {reportDone ? (
              <>
                <h3 className={styles.sheetTitle}>Thanks, our team will review this.</h3>
                <p className={styles.sheetSub}>We appreciate you helping keep the marketplace safe.</p>
                <div className={styles.sheetActions}>
                  <button type="button" className={styles.primaryBtn} onClick={closeReport}>Done</button>
                </div>
              </>
            ) : (
              <>
                <h3 className={styles.sheetTitle}>Report this listing</h3>
                <p className={styles.sheetSub}>Tell us what’s wrong. Reports are anonymous.</p>
                <div className={styles.radioList}>
                  {REPORT_REASONS.map((r) => (
                    <label key={r.value}
                      className={`${styles.radioItem} ${reportReason === r.value ? styles.radioItemActive : ''}`}>
                      <input type="radio" name="reportReason" value={r.value}
                        checked={reportReason === r.value} onChange={() => setReportReason(r.value)} />
                      {r.label}
                    </label>
                  ))}
                </div>
                <textarea className={styles.textarea} placeholder="Add a note (optional)"
                  value={reportNote} onChange={(e) => setReportNote(e.target.value)}
                  style={{ minHeight: 70, marginTop: 4 }} />
                {reportError && <p className={styles.error}>{reportError}</p>}
                <div className={styles.sheetActions}>
                  <button type="button" className={styles.secondaryBtn} onClick={closeReport} disabled={submittingReport}>Cancel</button>
                  <button type="button" className={styles.primaryBtn} onClick={submitReport}
                    disabled={!reportReason || submittingReport}>
                    {submittingReport ? 'Submitting…' : 'Submit report'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Block confirm */}
      {confirmBlock && listing && (
        <div className={styles.sheetOverlay} onClick={() => !blocking && setConfirmBlock(false)}>
          <div className={styles.sheet} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <h3 className={styles.sheetTitle}>Block {listing.seller.displayName}?</h3>
            <p className={styles.sheetSub}>
              You won’t see their listings or receive messages from them. You can unblock them later from their profile.
            </p>
            <div className={styles.sheetActions}>
              <button type="button" className={styles.secondaryBtn} onClick={() => setConfirmBlock(false)} disabled={blocking}>Cancel</button>
              <button type="button" className={styles.dangerBtn} onClick={blockSeller} disabled={blocking}>
                {blocking ? 'Blocking…' : 'Block seller'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className={styles.toast}>{toast}</div>}

      <Footer />
    </>
  );
}
